import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGS, type Lang } from "@/seo/routes";
import { headHtml, ssrHead, type HeadData } from "@/seo/head";
import type { ArticleLd, FaqItem } from "@/seo/jsonld";

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  /** Publishable FAQ (filter with publishableFaq) for FAQPage JSON-LD. */
  faq?: FaqItem[];
  /** Resources/cases: Article JSON-LD. */
  article?: ArticleLd;
}

const BRAND = "Techtail";
const DEFAULT_TITLE = "Techtail — Tiendas y Almacenes Autónomos 24/7";
const DEFAULT_DESCRIPTION =
  "Diseñamos, fabricamos e instalamos tiendas y almacenes autónomos 24/7 con visión artificial: Retail, Industrial y Medical. Desde Barcelona.";

const MANAGED =
  'link[rel="canonical"], link[rel="alternate"][hreflang], meta[property^="og:"], meta[name^="twitter:"], meta[name="description"], meta[name="keywords"], meta[name="robots"], meta[name="googlebot"], script[data-seo="page"]';

/**
 * Per-page head. During the build-time prerender it records its data in `ssrHead`
 * (written into static HTML); in the browser it keeps the head in sync on navigation.
 */
const SEOHead = ({ title, description, keywords, canonicalPath, ogImage, ogType = "website", noIndex = false, faq, article }: SEOHeadProps) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const raw = i18n.language?.substring(0, 2);
  const lang: Lang = (LANGS as readonly string[]).includes(raw) ? (raw as Lang) : "es";

  const rawPath = canonicalPath || location.pathname;
  const basePath = rawPath.replace(/^\/(es|en|it|de|fr)(?=\/|$)/, "").replace(/\/+$/, "");

  const cleanTitle = title?.replace(/\s*[|·—-]\s*TECHTAIL\s*$/i, "").trim();
  const fullTitle = cleanTitle ? (/techtail/i.test(cleanTitle) ? cleanTitle : `${cleanTitle} | ${BRAND}`) : DEFAULT_TITLE;
  const name = (cleanTitle || BRAND).replace(/\s*[|·—-]\s*Techtail\s*$/i, "").trim() || BRAND;

  const data: HeadData = {
    lang,
    basePath,
    title: fullTitle,
    name,
    description: description || DEFAULT_DESCRIPTION,
    keywords,
    ogImage,
    ogType,
    noIndex,
    faq,
    article,
  };

  if (typeof window === "undefined") ssrHead.current = data;

  const html = headHtml(data);

  useEffect(() => {
    document.title = fullTitle;
    document.documentElement.lang = lang;
    document.head.querySelectorAll(MANAGED).forEach((el) => el.remove());
    const tpl = document.createElement("template");
    tpl.innerHTML = html.replace(/<title>[\s\S]*?<\/title>/, "");
    document.head.appendChild(tpl.content);
  }, [html, fullTitle, lang]);

  return null;
};

export default SEOHead;
